import { IconType } from 'react-icons'
import { MdHelpOutline } from 'react-icons/md'

interface FinancialStatCardProps {
    title: string
    value: number
    subtitle?: string
    icon: IconType
    color: 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary'
    format?: 'number' | 'currency'
    /** Abre a explicação do card (o que soma, por qual data e por quê). */
    onHelp?: () => void
}

const formatCurrencyCompact = (cents: number) => {
    const value = cents / 100
    const abs = Math.abs(value)
    if (abs >= 1_000_000) {
        return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', {
            maximumFractionDigits: 2,
        })} mi`
    }
    if (abs >= 10_000) {
        return `R$ ${(value / 1_000).toLocaleString('pt-BR', {
            maximumFractionDigits: 1,
        })} mil`
    }
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value)
}

const formatCurrencyFull = (cents: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(cents / 100)

export default function FinancialStatCard({
    title,
    value,
    subtitle,
    icon: Icon,
    color,
    format = 'currency',
    onHelp,
}: FinancialStatCardProps) {
    const display =
        format === 'currency'
            ? formatCurrencyCompact(value)
            : new Intl.NumberFormat('pt-BR').format(value)

    const fullTitle =
        format === 'currency' ? formatCurrencyFull(value) : String(value)

    return (
        <div
            className={`group relative overflow-hidden rounded-xl bg-gradient-to-br from-base-100 to-base-200/50 border border-base-300/50 hover:border-${color}/30 transition-all duration-300 hover:shadow-md`}
        >
            <div className="absolute top-0 right-0 w-20 h-20 opacity-5">
                <Icon className="text-[5rem] -rotate-12" />
            </div>

            <div className="relative p-4 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-1">
                    <div className={`inline-flex p-2 rounded-lg bg-${color}/10 w-fit`}>
                        <Icon className={`text-base text-${color}`} />
                    </div>
                    {onHelp && (
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs btn-circle text-base-content/40 hover:text-base-content"
                            onClick={onHelp}
                            aria-label={`O que é ${title}?`}
                            title={`O que é ${title}?`}
                        >
                            <MdHelpOutline className="text-base" />
                        </button>
                    )}
                </div>

                <div className="min-w-0">
                    <p className="text-[10px] font-medium text-base-content/50 uppercase tracking-wide truncate">
                        {title}
                    </p>
                    <p
                        className="text-lg font-bold text-base-content mt-0.5 truncate"
                        title={fullTitle}
                    >
                        {display}
                    </p>
                    {subtitle && (
                        <p className="text-[11px] text-base-content/60 truncate">
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}
